import React, { useMemo } from 'react';
import { getScheduleChangesDescription, getBilledForColumnData } from '@css-xp/base/entitlement-utils';
import { sentenceCase } from '@atlassian/cssxp-utils-generic';
import { FormattedDate, DynamicResultTable, useFeatureToggle } from '@css-xp/base';
import { ChargeQuantity, SubscriptionScheduleAction } from '@css-xp/graphtypes/src/generated-types-new';
import { ShowUpdatedText } from '@css-xp/entitlements-v2';
import { emptyStateProp, genericEmptyStateProp, NoData, TableHeading } from '@css-xp/ppc-common';
import './scheduled-changes-tab.scss';

interface Props {
  changeDate: number | null;
  changeAction?: SubscriptionScheduleAction | null;
  oldValues: ScheduledChangeComparisonFields;
  newValues: ScheduledChangeComparisonFields;
  scheduledQuantity?: ChargeQuantity[];
  units?: string | null;
  productId?: string | null;
  isDesignIteration1Enabled?: boolean;
}

interface ScheduledChangeComparisonFields {
  offering?: string | null;
  pricingPlan?: string | null;
  billingFrequency?: string | null;
}

export const ScheduledChangesTab = ({
  changeDate,
  changeAction,
  oldValues,
  newValues,
  scheduledQuantity,
  units,
  productId,
  isDesignIteration1Enabled,
}: Props) => {
  const [isEnabled] = useFeatureToggle();
  const headData = useMemo(() => {
    const cells = [
      {
        content: 'Effective',
      },
      {
        content: 'Description',
      },
      {
        content: 'Plan',
      },
      {
        content: 'Pricing',
      },
      {
        content: 'Billing cycle',
      },
      {
        content: 'Billed for',
      },
    ];

    return {
      cells,
    };
  }, []);

  const createRows = (): Array<any> => {
    const rowCells = [
      {
        content: (
          <>{changeDate ? <FormattedDate date={new Date(changeDate).toISOString()} showTime={true} /> : <NoData />}</>
        ),
      },
      {
        content: getScheduleChangesDescription(changeAction),
      },
      {
        content: <ShowUpdatedText before={sentenceCase(oldValues.offering)} after={sentenceCase(newValues.offering)} />,
      },
      {
        content: (
          <span>
            <ShowUpdatedText before={sentenceCase(oldValues.pricingPlan)} after={sentenceCase(newValues.pricingPlan)} />
          </span>
        ),
      },
      {
        content: (
          <span>
            {' '}
            <ShowUpdatedText
              before={sentenceCase(oldValues.billingFrequency)}
              after={sentenceCase(newValues.billingFrequency)}
            />
          </span>
        ),
      },
      {
        content:
          scheduledQuantity && scheduledQuantity.length > 0 ? (
            <ShowUpdatedText
              before={units ? units + ' ' + getBilledForColumnData(productId, oldValues.offering) : ''}
              after={`${scheduledQuantity[0].quantity}` + ' ' + getBilledForColumnData(productId, newValues.offering)}
            />
          ) : (
            <NoData />
          ),
      },
    ];

    return [
      {
        key: 'schedule-tab-row',
        cells: rowCells,
      },
    ];
  };

  return (
    <div className="scheduled-changes">
      <DynamicResultTable
        beforeTable={isDesignIteration1Enabled ? null : <TableHeading>Scheduled changes</TableHeading>}
        testId="entitlement-scheduled-changes"
        head={headData}
        rows={createRows()}
        emptyStateProps={isDesignIteration1Enabled ? genericEmptyStateProp : emptyStateProp}
        isPaginationEnabled={false}
        hideHeadOnEmptyState={isDesignIteration1Enabled}
      />
    </div>
  );
};
